import { useForm } from '@tanstack/react-form';
import { toast } from 'sonner';
import { z } from 'zod';

import {
  Button,
  DialogFooter,
  Input,
  Label,
  Textarea,
} from '@virtual-care/shadcn';
import {
  FieldMessage,
  FormMessage,
  TheButton,
  errorToFormIssue,
  useAction,
} from '@virtual-care/ui';

import { CreateDialog } from '../../components/create-dialog';

const schema = z.object({
  name: z.string().min(1, { message: 'Name is required' }),
  nameEn: z.string(),
  description: z.string().min(1, { message: 'Description is required' }),
  descriptionEn: z.string(),
  insuranceCode: z.string().min(1, { message: 'Insurance code is required' }),
});

export function CreateSpeciality({
  trigger,
  onSave,
  ...props
}: {
  trigger: React.ReactNode;
  onSave?: () => void;
  open?: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const createSpeciality = useAction('POST /specialities', {
    invalidate: ['GET /specialities'],
  });
  const form = useForm({
    defaultValues: {
      name: '',
      nameEn: '',
      description: '',
      descriptionEn: '',
      insuranceCode: '',
    },
    validators: {
      onSubmit: schema,
      onSubmitAsync: async ({ value }) => {
        try {
          await createSpeciality.mutateAsync({
            name: value.name.trim(),
            nameEn: value.nameEn.trim() || undefined,
            description: value.description.trim(),
            descriptionEn: value.descriptionEn.trim() || undefined,
            insuranceCode: value.insuranceCode.trim(),
          });
          props.onOpenChange(false);
          toast.success('Speciality created successfully');
          return void 0;
        } catch (error) {
          return errorToFormIssue(error);
        }
      },
    },
  });

  return (
    <CreateDialog
      form={form}
      open={props.open}
      onOpenChange={props.onOpenChange}
      trigger={trigger}
      title="Create Speciality"
      subtitle="Add a new medical speciality to the system."
      onSave={onSave}
    >
      <form.Field
        name="name"
        children={(field) => (
          <div className="space-y-2">
            <Label htmlFor="name">Arabic Name</Label>
            <Input
              id="name"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
              placeholder="طب القلب"
            />
            <FieldMessage errors={field.state.meta.errors} />
          </div>
        )}
      />

      <form.Field
        name="nameEn"
        children={(field) => (
          <div className="space-y-2">
            <Label htmlFor="nameEn">English Name</Label>
            <Input
              id="nameEn"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
              placeholder="Cardiology"
            />
            <FieldMessage errors={field.state.meta.errors} />
          </div>
        )}
      />

      <form.Field
        name="description"
        children={(field) => (
          <div className="space-y-2">
            <Label htmlFor="description">Arabic Description</Label>
            <Textarea
              id="description"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
              placeholder="يعالج اضطرابات القلب والأوعية الدموية"
              rows={4}
            />
            <FieldMessage errors={field.state.meta.errors} />
          </div>
        )}
      />

      <form.Field
        name="descriptionEn"
        children={(field) => (
          <div className="space-y-2">
            <Label htmlFor="descriptionEn">English Description</Label>
            <Textarea
              id="descriptionEn"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
              placeholder="Deals with disorders of the heart and blood vessels"
              rows={4}
            />
            <FieldMessage errors={field.state.meta.errors} />
          </div>
        )}
      />

      <form.Field
        name="insuranceCode"
        children={(field) => (
          <div className="space-y-2">
            <Label htmlFor="insuranceCode">Insurance Code</Label>
            <Input
              id="insuranceCode"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
              placeholder="e.g., TC001"
            />
            <FieldMessage errors={field.state.meta.errors} />
          </div>
        )}
      />
      <form.Subscribe
        selector={(state) => state.errors}
        children={(errors) => <FormMessage errors={errors} />}
      />
      <DialogFooter>
        <form.Subscribe
          selector={(state) => [state.isSubmitSuccessful]}
          children={([isSubmitSuccessful]) =>
            isSubmitSuccessful ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  props.onOpenChange(false);
                  form.reset();
                }}
              >
                Close
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                onClick={() => props.onOpenChange(false)}
              >
                Cancel
              </Button>
            )
          }
        />
        <form.Subscribe
          selector={(state) =>
            [
              state.canSubmit,
              state.isSubmitting,
              state.isSubmitSuccessful,
            ] as const
          }
          children={([canSubmit, isSubmitting, isSubmitSuccessful]) => (
            <TheButton
              type="submit"
              loading={isSubmitting}
              disabled={!canSubmit || isSubmitting || isSubmitSuccessful}
            >
              {isSubmitting ? 'Creating...' : 'Create'}
            </TheButton>
          )}
        />
      </DialogFooter>
    </CreateDialog>
  );
}
